/**
 * Animation Orchestrator for FlowRide Frontend
 * Centralizes all animation loops into a single requestAnimationFrame.
 * Each registered callback can have its own FPS limit while sharing the same RAF loop.
 * Also provides centralized FPS tracking per callback.
 *
 * Additionally coordinates map repaint requests: multiple requestRepaint() calls
 * within a single frame are batched into a single triggerRepaint() call.
 */

import maplibregl from "maplibre-gl";

type AnimationCallback = ( deltaTime: number, timestamp: number ) => void;

type FpsUpdateCallback = ( fps: number ) => void;

interface RegisteredCallback {
    "id":              string;
    "callback":        AnimationCallback;
    "maxFps":          number;
    "lastCallTime":    number;
    "minInterval":     number;
    "frameCount":      number;
    "lastFpsUpdateMs": number;
    "currentFps":      number;
    "onFpsUpdate":     FpsUpdateCallback | null;
}

/**
 * Singleton class that manages a single requestAnimationFrame loop
 * and dispatches to registered animation callbacks with individual FPS throttling.
 */
class AnimationOrchestrator {

    private static instance: AnimationOrchestrator | null = null;

    /** Map of registered animation callbacks */
    private callbacks: Map<string, RegisteredCallback> = new Map();

    /** Whether the animation loop is currently running */
    private isRunning: boolean = false;

    /** Current requestAnimationFrame ID */
    private rafId: number | null = null;

    /** Timestamp of the last frame */
    private lastFrameTime: number = 0;

    /** Reference to the MapLibre map instance for coordinated repaints */
    private map: maplibregl.Map | null = null;

    /** Flag indicating a repaint is needed this frame */
    private needsRepaint: boolean = false;

    /**
     * Get the singleton instance of AnimationOrchestrator
     */
    static getInstance(): AnimationOrchestrator {
        if ( AnimationOrchestrator.instance === null ) {
            AnimationOrchestrator.instance = new AnimationOrchestrator();
        }

        return AnimationOrchestrator.instance;
    }

    /**
     * Register an animation callback
     * @param id - Unique identifier for this callback
     * @param callback - Function called each frame (receives deltaTime in ms and timestamp)
     * @param maxFps - Maximum FPS for this callback (0 = unlimited, runs every frame)
     * @param onFpsUpdate - Optional callback to receive FPS updates (called every second)
     */
    register( id: string, callback: AnimationCallback, maxFps: number = 60, onFpsUpdate: FpsUpdateCallback | null = null ): void {
        this.callbacks.set( id, {
            id,
            callback,
            maxFps,
            "lastCallTime":    0,
            "minInterval":     maxFps > 0 ? 1000 / maxFps : 0,
            "frameCount":      0,
            "lastFpsUpdateMs": 0,
            "currentFps":      0,
            onFpsUpdate,
        } );

        // Auto-start when first callback registered
        if ( this.callbacks.size === 1 && !this.isRunning ) {
            this.start();
        }
    }

    /**
     * Unregister an animation callback
     * @param id - Unique identifier of the callback to remove
     */
    unregister( id: string ): void {
        this.callbacks.delete( id );

        // Auto-stop when no more callbacks
        if ( this.callbacks.size === 0 ) {
            this.stop();
        }
    }

    /**
     * Check if a callback is registered
     * @param id - Unique identifier to check
     */
    isRegistered( id: string ): boolean {
        return this.callbacks.has( id );
    }

    /**
     * Update maxFps for an existing callback
     * @param id - Unique identifier of the callback
     * @param maxFps - New maximum FPS
     */
    setMaxFps( id: string, maxFps: number ): void {
        const registered = this.callbacks.get( id );
        if ( registered !== undefined ) {
            registered.maxFps = maxFps;
            registered.minInterval = maxFps > 0 ? 1000 / maxFps : 0;
        }
    }

    /**
     * Set or update the FPS update callback for a registered animation
     * @param id - Unique identifier of the callback
     * @param onFpsUpdate - Callback to receive FPS updates, or null to remove
     */
    setOnFpsUpdate( id: string, onFpsUpdate: FpsUpdateCallback | null ): void {
        const registered = this.callbacks.get( id );
        if ( registered !== undefined ) {
            registered.onFpsUpdate = onFpsUpdate;
        }
    }

    /**
     * Get the current FPS for a registered callback
     * @param id - Unique identifier of the callback
     * @returns Current FPS, or 0 if callback not found
     */
    getFps( id: string ): number {
        const registered = this.callbacks.get( id );

        return registered?.currentFps ?? 0;
    }

    /**
     * Get the number of registered callbacks
     */
    getCallbackCount(): number {
        return this.callbacks.size;
    }

    /**
     * Get all registered callback IDs (for debugging)
     */
    getRegisteredIds(): string[] {
        return Array.from( this.callbacks.keys() );
    }

    /**
     * Check if the animation loop is running
     */
    getIsRunning(): boolean {
        return this.isRunning;
    }

    /**
     * Set the MapLibre map instance for coordinated repaints.
     * Must be called before requestRepaint() can trigger actual repaints.
     * @param map - The MapLibre map instance, or null to clear
     */
    setMap( map: maplibregl.Map | null ): void {
        this.map = map;
    }

    /**
     * Request a map repaint. Multiple calls within the same frame are batched when the
     * orchestrator loop is running. If the loop is stopped, flush immediately so idle
     * marker/icon updates still paint without requiring a user pan.
     */
    requestRepaint(): void {
        this.needsRepaint = true;

        if ( !this.isRunning && this.map !== null ) {
            this.needsRepaint = false;
            this.map.triggerRepaint();
        }
    }

    /**
     * Start the animation loop
     */
    private start(): void {
        if ( this.isRunning ) {
            return;
        }
        this.isRunning = true;
        this.lastFrameTime = performance.now();
        this.loop();
    }

    /**
     * Stop the animation loop
     */
    private stop(): void {
        this.isRunning = false;
        if ( this.rafId !== null ) {
            cancelAnimationFrame( this.rafId );
            this.rafId = null;
        }
    }

    /**
     * Main animation loop - called via requestAnimationFrame
     * Dispatches to all registered callbacks respecting their individual FPS limits
     * Also tracks FPS per callback
     */
    private readonly loop = (): void => {
        if ( !this.isRunning ) {
            return;
        }

        const now = performance.now();
        const deltaTime = now - this.lastFrameTime;
        this.lastFrameTime = now;

        // Call all registered callbacks (respecting their individual throttle)
        for ( const registered of this.callbacks.values() ) {
            const shouldCall = registered.minInterval <= 0 ||
              now - registered.lastCallTime >= registered.minInterval;

            if ( shouldCall ) {
                registered.lastCallTime = now;

                // Track FPS for this callback
                if ( registered.lastFpsUpdateMs === 0 ) {
                    registered.lastFpsUpdateMs = now;
                }
                registered.frameCount++;
                const fpsElapsed = now - registered.lastFpsUpdateMs;
                if ( fpsElapsed >= 1000 ) {
                    registered.currentFps = Math.round( ( registered.frameCount / fpsElapsed ) * 1000 );
                    registered.frameCount = 0;
                    registered.lastFpsUpdateMs = now;

                    if ( registered.onFpsUpdate !== null ) {
                        registered.onFpsUpdate( registered.currentFps );
                    }
                }

                // Execute the callback
                try {
                    registered.callback( deltaTime, now );
                } catch( error ) {
                    console.error( `[AnimationOrchestrator] Callback "${ registered.id }" error:`, error );
                }
            }
        }

        // Batch all repaint requests into a single triggerRepaint() call
        if ( this.needsRepaint && this.map !== null ) {
            this.needsRepaint = false;
            this.map.triggerRepaint();
        }

        this.rafId = requestAnimationFrame( this.loop );
    };

}

/** Singleton instance of the AnimationOrchestrator */
export const animationOrchestrator = AnimationOrchestrator.getInstance();
